<script>
  import ProfileLinkCard from './ProfileLinkCard.svelte'
  import { BENTO_ACCENTS, BRUTAL_COLORS } from '@/lib/profile-themes'

  /**
   * @type {{
   *   links: Array<{ title: string, description?: string, url: string, emoji?: string, thumbnailUrl?: string, type: 'post' | 'external' | 'file' | 'headline', clicks?: number, date?: string }>,
   *   layout: 'default' | 'bento' | 'brutal' | 'nature' | 'minimal' | 'corporate' | 'ocean',
   *   theme?: string
   * }}
   *
   * The 'headline' link type is a non-interactive section divider — the
   * user puts one between groups of links to label them. It renders as
   * centered text with a divider rule, not a clickable card.
   */
  let { links = [], layout = 'default', theme = 'clean' } = $props()

  function getTypeIcon(type) {
    if (type === 'external') return 'M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
    if (type === 'file') return 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z'
    return 'M9 5l7 7-7 7'
  }

  function cardVariant(layout) {
    if (layout === 'corporate') return 'corporate'
    if (layout === 'ocean') return 'ocean'
    if (layout === 'brutal') return 'brutal'
    if (layout === 'nature') return 'nature'
    return 'default'
  }
</script>

{#if layout === 'bento'}
  <div class="bento-grid">
    {#each links as link, i}
      <a
        href={link.url}
        class="bento-card"
        class:bento-wide={i === 0 || i === 4}
        style="--bento-accent: {BENTO_ACCENTS[i % BENTO_ACCENTS.length]}"
      >
        <div class="bento-accent-strip"></div>
        <div class="bento-content">
          {#if link.emoji}
            <span class="bento-emoji">{link.emoji}</span>
          {/if}
          <div class="bento-text">
            <span class="bento-title">{link.title}</span>
            {#if link.description}
              <span class="bento-desc">{link.description}</span>
            {/if}
          </div>
          {#if link.clicks}
            <span class="bento-clicks">{link.clicks}</span>
          {/if}
        </div>
      </a>
    {/each}
  </div>
{:else if layout === 'minimal'}
  <div class="minimal-links">
    {#each links as link}
      <a href={link.url} class="minimal-link">
        <div class="minimal-link-left">
          {#if link.emoji}
            <span class="minimal-emoji">{link.emoji}</span>
          {/if}
          <div class="minimal-text">
            <span class="minimal-title">{link.title}</span>
            {#if link.date}
              <span class="minimal-date">{link.date}</span>
            {/if}
          </div>
        </div>
        <svg class="minimal-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d={getTypeIcon(link.type)} />
        </svg>
      </a>
    {/each}
  </div>
{:else}
  <div class="links-list">
    {#each links as link, i}
      {#if link.type === 'headline'}
        <div class="link-headline" role="heading" aria-level="2">
          {#if link.emoji}
            <span class="link-headline-emoji" aria-hidden="true">{link.emoji}</span>
          {/if}
          <span class="link-headline-text">{link.title}</span>
        </div>
      {:else}
        <ProfileLinkCard
          title={link.title}
          description={link.description}
          url={link.url}
          emoji={link.emoji}
          thumbnailUrl={link.thumbnailUrl}
          type={link.type}
          clicks={link.clicks}
          date={link.date}
          {theme}
          variant={cardVariant(layout)}
          accentColor={layout === 'brutal' ? BRUTAL_COLORS[i % BRUTAL_COLORS.length] : ''}
        />
      {/if}
    {/each}
  </div>
{/if}

<style>
  .links-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  /* Section headline divider — non-interactive label that groups the
     links below it. Uses centered text with a thin rule on each side so
     the visual hierarchy is obvious without taking as much vertical
     space as a full card. Respects the theme text color token. */
  .link-headline {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin: 12px 4px 2px;
    color: var(--text-secondary);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .link-headline::before,
  .link-headline::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--divider, color-mix(in srgb, var(--text-secondary) 20%, transparent));
  }
  .link-headline-emoji {
    font-size: 14px;
  }
  .link-headline-text {
    white-space: nowrap;
  }

  /* ===== Bento Grid ===== */
  .bento-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .bento-card {
    background: var(--card-bg);
    border-radius: var(--card-radius);
    box-shadow: var(--card-shadow);
    text-decoration: none;
    overflow: hidden;
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    position: relative;
  }

  .bento-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }

  .bento-wide {
    grid-column: span 2;
  }

  .bento-accent-strip {
    height: 3px;
    background: var(--bento-accent);
    width: 100%;
  }

  .bento-content {
    padding: 16px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .bento-emoji {
    font-size: 22px;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .bento-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .bento-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.4;
  }

  .bento-desc {
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.5;
  }

  .bento-clicks {
    font-size: 11px;
    color: var(--text-tertiary);
    font-weight: 500;
    flex-shrink: 0;
    margin-top: 2px;
  }

  /* ===== Minimal Links ===== */
  .minimal-links {
    display: flex;
    flex-direction: column;
  }

  .minimal-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0;
    border-bottom: 1px solid var(--divider);
    text-decoration: none;
    transition: opacity 150ms ease;
    cursor: pointer;
  }

  .minimal-link:first-child {
    border-top: 1px solid var(--divider);
  }

  .minimal-link:hover {
    opacity: 0.6;
  }

  .minimal-link-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }

  .minimal-emoji {
    font-size: 18px;
    flex-shrink: 0;
  }

  .minimal-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .minimal-title {
    font-size: 15px;
    font-weight: 500;
    color: var(--text-primary);
    letter-spacing: -0.1px;
  }

  .minimal-date {
    font-size: 12px;
    color: var(--text-secondary);
  }

  .minimal-arrow {
    width: 16px;
    height: 16px;
    color: var(--text-tertiary);
    flex-shrink: 0;
  }
</style>
